import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Select,
  InputAdornment,
  Divider,
  Typography,
  Button,
  Tooltip,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import ProgressModal from "../ProgressModal";

import { Edit as EditIcon, Search as SearchIcon, Stars as PlanDedicatedIcon } from "@material-ui/icons";

import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";

import moment from "moment";
import { i18n } from "../../translate/i18n";
import DedicatedPlanFields from "../DedicatedPlanFields";
import {
  buildPlanFormFromApi,
  buildDedicatedPlanSavePayload
} from "../../utils/dedicatedPlanFormState";

const companyRowHasDedicatedPlan = (row) => {
  const d = row?.plan?.dedicatedCompanyId;
  if (d != null && Number(d) > 0) return true;
  const name = row?.plan?.name;
  if (typeof name === "string" && /^PlanEmpresa\(\d+\)\s*$/i.test(name.trim())) return true;
  return false;
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: "2px"
  },
  mainPaper: {
    width: "100%",
    flex: 1,
    // padding: theme.spacing(2), //comentado para retirar o scroll do Empresas
  },
  fullWidth: {
    width: "100%",
  },
  tableContainer: {
    width: "100%",
    // overflowX: "scroll",
    // ...theme.scrollbarStyles,
    padding: "2px",
  },
  textfield: {
    width: "100%",
  },
  textRight: {
    textAlign: "right",
  },
  row: {
    // paddingTop: theme.spacing(2),
    // paddingBottom: theme.spacing(2),
  },
  control: {
    // paddingRight: theme.spacing(1),
    // paddingLeft: theme.spacing(1),
  },
  buttonContainer: {
    textAlign: "right",
    // padding: theme.spacing(1),
  },
}));

export function CompanyForm(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading, searchTerm, setSearchTerm, filteredCount } = props;
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});
  const [planForm, setPlanForm] = useState(null);
  const [planIsDedicated, setPlanIsDedicated] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    // campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    password: "",
    generateInvoice: true,
    ...initialValue,
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRecord((prev) => {
      if (moment(initialValue).isValid()) {
        initialValue.dueDate = moment(initialValue.dueDate).format(
          "YYYY-MM-DD"
        );
      }
      return {
        ...prev,
        ...initialValue,
      };
    });
  }, [initialValue]);

  useEffect(() => {
    const id = initialValue?.id;
    if (!id) {
      setPlanForm(null);
      setPlanIsDedicated(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/companies/listPlan/${id}`);
        if (cancelled) return;
        if (data.plan) {
          setPlanForm(buildPlanFormFromApi(data.plan));
          setPlanIsDedicated(!!data.planIsDedicated);
        } else {
          setPlanForm(null);
          setPlanIsDedicated(false);
        }
      } catch (err) {
        if (!cancelled) {
          setPlanForm(null);
          setPlanIsDedicated(false);
          toastError(err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialValue?.id]);

  const handleSaveDedicatedPlan = async () => {
    if (!initialValue?.id || !planForm) return;
    setSavingPlan(true);
    try {
      const payload = buildDedicatedPlanSavePayload(planForm);
      await api.put(`/companies/${initialValue.id}/dedicated-plan`, payload);
      toast.success("Plano personalizado salvo (PlanEmpresa).");
      const { data } = await api.get(`/companies/listPlan/${initialValue.id}`);
      if (data.plan) {
        setPlanForm(buildPlanFormFromApi(data.plan));
        setPlanIsDedicated(!!data.planIsDedicated);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSavingPlan(false);
    }
  };

  const handleSubmit = async (data) => {
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }
    console.log("Dados do formulário:", data);
    onSubmit(data);
    setRecord({ ...initialValue, dueDate: "", generateInvoice: true });
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  const incrementDueDate = () => {
    const data = { ...record };
    if (data.dueDate !== "" && data.dueDate !== null) {
      switch (data.recurrence) {
        case "MENSAL":
          data.dueDate = moment(data.dueDate)
            .add(1, "month")
            .format("YYYY-MM-DD");
          break;
        case "BIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(2, "month")
            .format("YYYY-MM-DD");
          break;
        case "TRIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(3, "month")
            .format("YYYY-MM-DD");
          break;
        case "SEMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(6, "month")
            .format("YYYY-MM-DD");
          break;
        case "ANUAL":
          data.dueDate = moment(data.dueDate)
            .add(12, "month")
            .format("YYYY-MM-DD");
          break;
        default:
          break;
      }
    }
    setRecord(data);
  };

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />
      <Formik
        enableReinitialize
        className={classes.fullWidth}
        initialValues={record}
        onSubmit={(values, { resetForm }) =>
          setTimeout(() => {
            handleSubmit(values);
            resetForm();
          }, 500)
        }
      >
        {(values, setValues) => (
          <Form className={classes.fullWidth}>
            <Grid spacing={1} justifyContent="center" container>
              <Grid xs={12} sm={6} md={3} item>
                <Field
                  as={TextField}
                  label={i18n.t("compaies.table.name")}
                  name="name"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <Field
                  as={TextField}
                  label={i18n.t("compaies.table.email")}
                  name="email"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                  required
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <Field
                  as={TextField}
                  label={i18n.t("compaies.table.password")}
                  name="password"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <Field
                  as={TextField}
                  label={i18n.t("compaies.table.phone")}
                  name="phone"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="plan-selection">{i18n.t("compaies.table.plan")}</InputLabel>
                  <Field
                    as={Select}
                    id="plan-selection"
                    label={i18n.t("compaies.table.plan")}
                    labelId="plan-selection-label"
                    name="planId"
                    margin="dense"
                    required
                    disabled={planIsDedicated}
                  >
                    {plans.map((plan, key) => (
                      <MenuItem key={key} value={plan.id}>
                        {plan.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
                {planIsDedicated && (
                  <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                    Plano dedicado: altere módulos e limites na seção abaixo (não troque o plano pelo catálogo).
                  </Typography>
                )}
              </Grid>
              <Grid xs={12} sm={6} md={1} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="status-selection">{i18n.t("compaies.table.active")}</InputLabel>
                  <Field
                    as={Select}
                    id="status-selection"
                    label={i18n.t("compaies.table.active")}
                    labelId="status-selection-label"
                    name="status"
                    margin="dense"
                  >
                    <MenuItem value={true}>{i18n.t("compaies.table.yes")}</MenuItem>
                    <MenuItem value={false}>{i18n.t("compaies.table.no")}</MenuItem>
                  </Field>
                </FormControl>
              </Grid>              
              {/* <Grid xs={12} sm={6} md={3} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="payment-method-selection">
                    Método de Pagamento
                  </InputLabel>
                  <Field
                    as={Select}
                    id="payment-method-selection"
                    label="Método de Pagamento"
                    labelId="payment-method-selection-label"
                    name="paymentMethod"
                    margin="dense"
                  >
                    <MenuItem value={"pix"}>PIX</MenuItem>
                  </Field>
                </FormControl>
              </Grid> */}
              <Grid xs={12} sm={6} md={2} item>
                <Field
                  as={TextField}
                  label={i18n.t("compaies.table.document")}
                  name="document"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                />
              </Grid>
              {/* <Grid xs={12} sm={6} md={2} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="status-selection">Campanhas</InputLabel>
                  <Field
                    as={Select}
                    id="campaigns-selection"
                    label="Campanhas"
                    labelId="campaigns-selection-label"
                    name="campaignsEnabled"
                    margin="dense"
                  >
                    <MenuItem value={true}>Habilitadas</MenuItem>
                    <MenuItem value={false}>Desabilitadas</MenuItem>
                  </Field>
                </FormControl>
              </Grid> */}
              <Grid xs={12} sm={6} md={2} item>
                <FormControl variant="outlined" fullWidth>
                  <Field
                    as={TextField}
                    label={i18n.t("compaies.table.dueDate")}
                    type="date"
                    name="dueDate"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    fullWidth
                    margin="dense"
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="recorrencia-selection">
                  {i18n.t("compaies.table.recurrence")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label="Recorrência"
                    labelId="recorrencia-selection-label"
                    id="recurrence"
                    name="recurrence"
                    margin="dense"
                  >
                    <MenuItem value="MENSAL">{i18n.t("compaies.table.monthly")}</MenuItem>
                    <MenuItem value="BIMESTRAL">{i18n.t("compaies.table.bimonthly")}</MenuItem>
                    <MenuItem value="TRIMESTRAL">{i18n.t("compaies.table.quarterly")}</MenuItem>
                    <MenuItem value="SEMESTRAL">{i18n.t("compaies.table.semester")}</MenuItem>
                    <MenuItem value="ANUAL">{i18n.t("compaies.table.yearly")}</MenuItem>
                  </Field>
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6} md={2} item>
                <FormControl margin="dense" variant="outlined" fullWidth>
                  <InputLabel htmlFor="generate-invoice-selection">
                    Gerar Fatura
                  </InputLabel>
                  <Field
                    as={Select}
                    id="generate-invoice-selection"
                    label="Gerar Fatura"
                    labelId="generate-invoice-selection-label"
                    name="generateInvoice"
                    margin="dense"
                  >
                    <MenuItem value={true}>Sim</MenuItem>
                    <MenuItem value={false}>Não</MenuItem>
                  </Field>
                </FormControl>
              </Grid>

              {initialValue?.id && user?.super && planForm && (
                <Grid xs={12} item>
                  <Divider style={{ margin: "8px 0 16px" }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Plano personalizado (somente aqui — não aparece na aba Planos). Nome:{" "}
                    <code>PlanEmpresa({initialValue.id})</code>
                    {planIsDedicated ? " — já dedicado" : " — primeira gravação cria o plano dedicado"}
                  </Typography>
                  <DedicatedPlanFields
                    companyId={initialValue.id}
                    planForm={planForm}
                    setPlanForm={setPlanForm}
                  />
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    disabled={savingPlan}
                    onClick={handleSaveDedicatedPlan}
                    style={{ marginTop: 12 }}
                  >
                    {savingPlan ? "Salvando…" : "Salvar personalização do plano"}
                  </Button>
                </Grid>
              )}

              <Grid xs={12} item>
                <Grid justifyContent="space-between" alignItems="center" spacing={1} container>
                  {/* Campo de Pesquisa */}
                  <Grid xs={12} md={4} item style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <TextField
                      placeholder="Pesquisar por ID, Nome, Email ou Telefone..."
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <span style={{ color: "#666", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                      {filteredCount} empresa(s)
                    </span>
                  </Grid>
                  
                  {/* Botões */}
                  <Grid xs={12} md={8} item>
                    <Grid justifyContent="flex-end" spacing={1} container>
                      <Grid xs={4} md={1} item>
                        <ButtonWithSpinner
                          className={classes.fullWidth}
                          style={{ marginTop: 7 }}
                          loading={loading}
                          onClick={() => onCancel()}
                          variant="contained"
                        >
                          {i18n.t("compaies.table.clear")}
                        </ButtonWithSpinner>
                      </Grid>
                  {record.id !== undefined ? (
                    <>
                      <Grid xs={6} md={1} item>
                        <ButtonWithSpinner
                          style={{ marginTop: 7 }}
                          className={classes.fullWidth}
                          loading={loading}
                          onClick={() => onDelete(record)}
                          variant="contained"
                          color="secondary"
                          disabled={record.id === 1}
                        >
                          {i18n.t("compaies.table.delete")}
                        </ButtonWithSpinner>
                      </Grid>
                      <Grid xs={6} md={2} item>
                        <ButtonWithSpinner
                          style={{ marginTop: 7 }}
                          className={classes.fullWidth}
                          loading={loading}
                          onClick={() => incrementDueDate()}
                          variant="contained"
                          color="primary"
                        >
                          {i18n.t("compaies.table.dueDate")}
                        </ButtonWithSpinner>
                      </Grid>
                      {/* <Grid xs={6} md={1} item>
                        <ButtonWithSpinner
                          style={{ marginTop: 7 }}
                          className={classes.fullWidth}
                          loading={loading}
                          onClick={() => handleOpenModalUsers()}
                          variant="contained"
                          color="primary"
                        >
                          {i18n.t("compaies.table.user")}
                        </ButtonWithSpinner>
                      </Grid> */}
                    </>
                  ) : null}
                      <Grid xs={6} md={1} item>
                        <ButtonWithSpinner
                          className={classes.fullWidth}
                          style={{ marginTop: 7 }}
                          loading={loading}
                          type="submit"
                          variant="contained"
                          color="primary"
                        >
                          {i18n.t("compaies.table.save")}
                        </ButtonWithSpinner>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
}

export function CompaniesManagerGrid(props) {
  const { records, onSelect } = props;
  const classes = useStyles();
  const { dateToClient, datetimeToClient } = useDate();

  const renderStatus = (row) => {
    return row.status === false ? "Não" : "Sim";
  };

  const renderPlan = (row) => {
    return row.planId !== null ? row.plan.name : "-";
  };

  const renderPlanValue = (row) => {
    return row.planId !== null ? row.plan.amount ? row.plan.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00' : "-";
  };

  // const renderCampaignsStatus = (row) => {
  //   if (
  //     has(row, "settings") &&
  //     isArray(row.settings) &&
  //     row.settings.length > 0
  //   ) {
  //     const setting = row.settings.find((s) => s.key === "campaignsEnabled");
  //     if (setting) {
  //       return setting.value === "true" ? "Habilitadas" : "Desabilitadas";
  //     }
  //   }
  //   return "Desabilitadas";
  // };

  const rowStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { backgroundColor: "#fffead" };
      }
      if (diff <= 0) {
        return { backgroundColor: "#fa8c8c" };
      }
    }
    return {};
  };
  
  const cellStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { color: "#000" }; // Texto preto para fundo amarelo
      }
      if (diff <= 0) {
        return { color: "#fff" }; // Texto branco para fundo vermelho
      }
    }
    return {};
  };

  const iconStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { color: "#000" }; // Ícone preto para fundo amarelo
      }
    }
    return {};
  };

  return (
    <Paper className={classes.tableContainer}>
      <Table
        className={classes.fullWidth}
        // size="small"
        padding="none"
        aria-label="a dense table"
      >
        <TableHead>
          <TableRow>
            <TableCell align="center" style={{ width: "1%" }}>#</TableCell>
            <TableCell align="center" style={{ width: "60px" }}>ID</TableCell>
            <TableCell align="left">{i18n.t("compaies.table.name")}</TableCell>
            <TableCell align="left">{i18n.t("compaies.table.email")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.phone")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.plan")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.value")}</TableCell>
            {/* <TableCell align="center">Campanhas</TableCell> */}
            <TableCell align="center">{i18n.t("compaies.table.active")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.createdAt")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.dueDate")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.lastLogin")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.generateInvoice")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row, key) => (
            <TableRow style={rowStyle(row)} key={key}>
              <TableCell style={{...cellStyle(row), width: "1%"}} align="center">
                <IconButton style={iconStyle(row)} onClick={() => onSelect(row)} aria-label="delete">
                  <EditIcon style={iconStyle(row)} />
                </IconButton>
              </TableCell>
              <TableCell style={{...cellStyle(row), fontWeight: "bold"}} align="center">{row.id}</TableCell>
              <TableCell style={cellStyle(row)} align="left">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {companyRowHasDedicatedPlan(row) ? (
                    <Tooltip title="Plano personalizado (dedicado a esta empresa — PlanEmpresa)">
                      <PlanDedicatedIcon
                        style={{
                          fontSize: 22,
                          color: cellStyle(row).color === "#fff" ? "#e1bee7" : "#7b1fa2",
                          flexShrink: 0,
                        }}
                        aria-label="Plano dedicado"
                      />
                    </Tooltip>
                  ) : null}
                  <span>{row.name || "-"}</span>
                </span>
              </TableCell>
              <TableCell style={cellStyle(row)} align="left" size="small">{row.email || "-"}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{row.phone || "-"}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{renderPlan(row)}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{i18n.t("compaies.table.money")} {renderPlanValue(row)}</TableCell>
              {/* <TableCell style={cellStyle(row)} align="center">{renderCampaignsStatus(row)}</TableCell> */}
              <TableCell style={cellStyle(row)} align="center">{renderStatus(row)}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{dateToClient(row.createdAt)}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{dateToClient(row.dueDate)}<br /><span>{row.recurrence}</span></TableCell>
              <TableCell style={cellStyle(row)} align="center">{datetimeToClient(row.lastLogin)}</TableCell>
              <TableCell style={cellStyle(row)} align="center">{row.generateInvoice ? i18n.t("compaies.table.yes") : i18n.t("compaies.table.no")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function CompaniesManager() {
  const classes = useStyles();
  const { list, save, update, remove, checkConnections } = useCompanies();
  const listRef = useRef(list);
  listRef.current = list;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressStatus, setProgressStatus] = useState("processing");
  const [progressMessage, setProgressMessage] = useState("");
  const [progressDetails, setProgressDetails] = useState(null);
  /** Apenas operações do formulário (salvar / excluir / vencimento); não misturar com fetch da tabela. */
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    // campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    password: "",
    document: "",
    paymentMethod: "",
    generateInvoice: true
  });

  const loadPlans = useCallback(async () => {
    try {
      const companyList = await listRef.current();
      setRecords(companyList);
    } catch (e) {
      toast.error("Não foi possível carregar a lista de registros");
    }
  }, []);

  const handleCancel = useCallback(() => {
    setRecord((prev) => ({
      ...prev,
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      dueDate: "",
      recurrence: "",
      password: "",
      document: "",
      paymentMethod: "",
      generateInvoice: true
    }));
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Escutar evento de exclusão concluída para atualizar lista
  useEffect(() => {
    const handleCompanyDeleted = (event) => {
      const { companyId } = event.detail;
      loadPlans();
      if (record.id === companyId) {
        handleCancel();
      }
    };

    window.addEventListener("company-deleted", handleCompanyDeleted);

    return () => {
      window.removeEventListener("company-deleted", handleCompanyDeleted);
    };
  }, [record.id, loadPlans, handleCancel]);

  const handleSubmit = async (data) => {
    console.log("Dados enviados para o backend:", data);
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error(
        "Não foi possível realizar a operação. Verifique se já existe uma empresa com o mesmo nome ou se os campos foram preenchidos corretamente"
      );
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    // Fechar modal de confirmação
    setShowConfirmDialog(false);
    
    // Abrir modal de progresso
    setShowProgressModal(true);
    setProgressStatus("processing");
    setProgressMessage("Iniciando exclusão da empresa...");
    setProgressDetails(null);
    
    const startTime = Date.now();
    
    try {
      // Atualizar status
      setProgressMessage("Verificando dados da empresa...");
      
      // Fazer a requisição de exclusão
      setProgressMessage("Iniciando exclusão em background...");
      setProgressDetails("A exclusão será processada em segundo plano. Você receberá uma notificação quando concluir.");
      
      const response = await remove(record.id);
      
      // Verificar se foi iniciado em background
      if (response?.status === "processing" || response?.jobId) {
        setProgressStatus("processing");
        setProgressMessage("Exclusão iniciada em background!");
        setProgressDetails(
          `A exclusão da empresa "${record.name}" foi iniciada em segundo plano.\n\n` +
          `Você receberá uma notificação no canto da tela com o progresso em tempo real.\n\n` +
          `Registros estimados: ${response.estimatedRecords || 'N/A'}\n\n` +
          `Você pode minimizar esta janela e continuar trabalhando.`
        );
        
        // Criar notificação inicial via evento customizado para o CompanyDeletionProgress
        // Isso garante que a notificação apareça mesmo se o modal for fechado rapidamente
        window.dispatchEvent(new CustomEvent('company-deletion-started', {
          detail: {
            companyId: parseInt(record.id),
            companyName: record.name,
            estimatedRecords: response.estimatedRecords || 0,
            jobId: response.jobId
          }
        }));
        
        // Fechar modal automaticamente após 2 segundos (mais rápido)
        setTimeout(() => {
          setShowProgressModal(false);
          setProgressStatus("processing");
          setProgressMessage("");
          setProgressDetails(null);
        }, 2000);
        
        // Recarregar lista após um tempo (para atualizar quando concluir)
        setTimeout(() => {
          loadPlans();
        }, 5000);
        
        return; // Não continuar com o código abaixo
      }
      
      const duration = Math.round((Date.now() - startTime) / 1000);
      
      // Sucesso
      setProgressStatus("success");
      setProgressMessage(`Empresa excluída com sucesso em ${duration} segundo(s)!`);
      setProgressDetails(`A empresa "${record.name}" (ID: ${record.id}) foi excluída permanentemente do sistema.`);
      
      // Recarregar lista e limpar formulário
      await loadPlans();
      handleCancel();
      
      // Fechar modal após 3 segundos em caso de sucesso
      setTimeout(() => {
        setShowProgressModal(false);
        setProgressStatus("processing");
        setProgressMessage("");
        setProgressDetails(null);
      }, 3000);
      
    } catch (e) {
      const duration = Math.round((Date.now() - startTime) / 1000);
      
      // Erro
      setProgressStatus("error");
      setProgressMessage("Erro ao excluir empresa");
      
      // Detalhes do erro
      let errorDetails = "Não foi possível realizar a operação.";
      let errorType = "Erro desconhecido";
      
      if (e?.code === 'ECONNABORTED' || e?.message?.includes('timeout') || e?.message?.includes('Network Error')) {
        errorType = "Timeout da Requisição";
        errorDetails = `A operação demorou mais de ${Math.round(duration / 60)} minuto(s) e foi cancelada. ` +
          `Isso geralmente acontece quando a empresa possui muitos dados relacionados (tickets, mensagens, contatos). ` +
          `O backend pode estar processando a exclusão, mas a requisição expirou antes de receber a resposta.`;
      } else if (e?.response?.data?.error) {
        errorDetails = e.response.data.error;
        errorType = "Erro do Servidor";
      } else if (e?.response?.status === 403) {
        errorType = "Permissão Negada";
        errorDetails = "Você não tem permissão para excluir empresas.";
      } else if (e?.response?.status === 404) {
        errorType = "Empresa Não Encontrada";
        errorDetails = "A empresa não foi encontrada no sistema.";
      } else if (e?.response?.status === 400) {
        errorType = "Erro de Validação";
        errorDetails = e.response.data?.error || "Não é possível excluir esta empresa devido a dados relacionados.";
      } else if (e?.message) {
        errorDetails = e.message;
      }
      
      setProgressDetails(
        `Tempo decorrido: ${duration} segundo(s)\n\n` +
        `Tipo de Erro: ${errorType}\n` +
        `Detalhes: ${errorDetails}\n\n` +
        `Possíveis causas:\n` +
        `- Empresa possui muitos dados relacionados (tickets, mensagens, contatos)\n` +
        `- Timeout da requisição (backend ainda processando)\n` +
        `- Problema de conexão com o servidor\n` +
        `- Permissões insuficientes\n\n` +
        `Sugestões:\n` +
        `- Aguarde alguns minutos e verifique se a empresa foi excluída\n` +
        `- Verifique os logs do servidor para mais detalhes\n` +
        `- Tente novamente mais tarde`
      );
      
      console.error("Erro ao excluir empresa:", e);
    }
    
    setLoading(false);
  };

  const handleOpenDeleteDialog = async () => {
    if (!record.id) return;
    
    setLoading(true);
    try {
      const connInfo = await checkConnections(record.id);
      setConnectionInfo(connInfo);
      setShowConfirmDialog(true);
    } catch (e) {
      toast.error("Erro ao verificar conexões da empresa");
    }
    setLoading(false);
  };

  const handleSelect = (data) => {
    // let campaignsEnabled = false;

    // const setting = data.settings.find(
    //   (s) => s.key.indexOf("campaignsEnabled") > -1
    // );
    // if (setting) {
    //   campaignsEnabled = setting.value === "true" || setting.value === "enabled";
    // }

    console.log("Dados da empresa selecionada:", data);
    setRecord((prev) => ({
      ...prev,
      id: data.id,
      name: data.name || "",
      phone: data.phone || "",
      email: data.email || "",
      planId: data.planId || "",
      status: data.status === false ? false : true,
      // campaignsEnabled,
      dueDate: data.dueDate || "",
      recurrence: data.recurrence || "",
      password: "",
      document: data.document || "",
      paymentMethod: data.paymentMethod || "",
      generateInvoice: data.generateInvoice !== undefined ? data.generateInvoice : true,
    }));
  };

  // Filtrar registros por nome, email, telefone ou ID
  const filteredRecords = records.filter((rec) => {
    const search = searchTerm.toLowerCase();
    const name = (rec.name || "").toLowerCase();
    const email = (rec.email || "").toLowerCase();
    const phone = (rec.phone || "").toLowerCase();
    const id = String(rec.id || "");
    
    return (
      name.includes(search) ||
      email.includes(search) ||
      phone.includes(search) ||
      id.includes(search)
    );
  });

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} item>
          <CompanyForm
            initialValue={record}
            onDelete={handleOpenDeleteDialog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filteredCount={filteredRecords.length}
          />
        </Grid>
        <Grid xs={12} item>
          <CompaniesManagerGrid records={filteredRecords} onSelect={handleSelect} />
        </Grid>
      </Grid>
      <ConfirmationModal
        title="Exclusão de Registro"
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setConnectionInfo(null);
        }}
        onConfirm={() => {
          handleDelete();
          setConnectionInfo(null);
        }}
      >
        <div>
          <p><strong>Empresa:</strong> {record.name}</p>
          <p><strong>ID:</strong> {record.id}</p>
          
          {connectionInfo && (
            <div style={{ marginTop: 16 }}>
              {connectionInfo.hasActiveConnections ? (
                <div style={{ 
                  backgroundColor: "#fff3cd", 
                  border: "1px solid #ffc107", 
                  borderRadius: 4, 
                  padding: 12,
                  marginBottom: 12
                }}>
                  <p style={{ color: "#856404", fontWeight: "bold", margin: 0 }}>
                    ⚠️ ATENÇÃO: Esta empresa possui {connectionInfo.activeConnections} conexão(ões) ATIVA(S)!
                  </p>
                </div>
              ) : null}
              
              <p><strong>Total de conexões:</strong> {connectionInfo.totalConnections}</p>
              <p><strong>Conexões ativas:</strong> {connectionInfo.activeConnections}</p>
              
              {connectionInfo.connections && connectionInfo.connections.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <p><strong>Lista de conexões:</strong></p>
                  <ul style={{ margin: 0, paddingLeft: 20, maxHeight: 150, overflowY: "auto" }}>
                    {connectionInfo.connections.map((conn) => (
                      <li key={conn.id} style={{ 
                        color: ["CONNECTED", "PAIRING", "OPENING", "qrcode"].includes(conn.status) 
                          ? "#28a745" 
                          : "#6c757d" 
                      }}>
                        <strong>{conn.name}</strong> - {conn.status} 
                        {conn.number !== "-" ? ` (${conn.number})` : ""}
                        {conn.channel !== "whatsapp" ? ` [${conn.channel}]` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <div style={{ 
            marginTop: 16, 
            padding: 12, 
            backgroundColor: "#f8d7da", 
            border: "1px solid #f5c6cb", 
            borderRadius: 4 
          }}>
            <p style={{ color: "#721c24", margin: 0 }}>
              <strong>⚠️ Esta ação é irreversível!</strong><br/>
              Todos os dados da empresa serão excluídos permanentemente, incluindo:
              tickets, contatos, filas, usuários, conexões e arquivos.
            </p>
          </div>
          
          <p style={{ marginTop: 16, fontWeight: "bold" }}>
            Deseja realmente excluir esta empresa?
          </p>
        </div>
      </ConfirmationModal>
      
      <ProgressModal
        open={showProgressModal}
        onClose={() => {
          console.log('[CompaniesManager] Modal minimizado, criando notificação no canto');
          setShowProgressModal(false);
          
          // Se estiver processando, garantir que a notificação apareça
          if (progressStatus === "processing" && record) {
            // Disparar evento novamente para garantir que a notificação apareça
            window.dispatchEvent(new CustomEvent('company-deletion-started', {
              detail: {
                companyId: parseInt(record.id),
                companyName: record.name,
                estimatedRecords: 0, // Será atualizado pelos eventos Socket.IO
                jobId: null
              }
            }));
          }
        }}
        title="Excluindo Empresa"
        status={progressStatus}
        message={progressMessage}
        details={progressDetails}
        showProgress={progressStatus === "processing"}
      />
    </Paper>
  );
}
